import { GetStaticPaths, GetStaticProps } from 'next';
import React, { ReactElement } from 'react';
import Template from '@components/layout/template';
import RenderBlocks from '@components/RenderBlocks';
import { API_URL, nullGetStaticProps } from 'lib/constants';
import { Page, Solution } from '@payload/types';
import InternalError from '@components/InternalError';
import NotFound from '@components/NotFound';
import Head from '@components/Head';

interface Props {
  page: Page;
  status?: number;
  isPreview: boolean;
}

export default function SolutionPage({
  page,
  status,
  isPreview,
}: Props): ReactElement {
  if (status === 500) {
    return <InternalError />;
  }

  if (!page) {
    return <NotFound />;
  }

  if (!isPreview && page.status !== 'published') {
    return <NotFound />;
  }

  const { options } = page;

  return (
    <React.Fragment>
      <Head
        title={`${page.meta?.title || page.title} | Telesoft`}
        description={page.meta?.description || page.description}
      >
        <meta
          property="og:title"
          content={`${page.meta?.title || page?.title} | Telesoft`}
          key="title"
        />
        <meta
          property="og:description"
          content={`${page.meta?.description || page?.summary}`}
          key="description"
        />
      </Head>
      <Template previewMode={isPreview} variant={options?.variant}>
        <RenderBlocks blocks={page.blocks} />
      </Template>
    </React.Fragment>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const res = await fetch(`${API_URL}/solutions?limit=500`);
  const json = await res.json();

  const solutions = json.docs;

  const paths = solutions.map((solution: Solution) => {
    return {
      params: { slug: solution.slug },
    };
  });

  return {
    paths: paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context?.params?.slug;
  const isPreview = !!context.preview;

  const res = await fetch(`${API_URL}/solutions?where[slug][equals]=${slug}`);
  if (!res.ok) {
    return {
      ...nullGetStaticProps,
      props: {
        status: 500,
      },
    };
  }

  const json = await res.json();
  if (json.totalDocs === 0) {
    return nullGetStaticProps;
  }

  return {
    props: { page: json.docs[0], isPreview },
    revalidate: 30,
  };
};
